import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToast } from 'vue-toast-notification'
import { cartApi } from '@/shared/api/cart'
import { useModalStore } from '@/features/modals/modalStore'
import { Merch } from '@/entities/preorder/model/types'
import { PreOrder } from '@/entities/product/model/types'

interface CartItem {
  id: number
  amount: number
  merch: {
    id: number
    title: string
    price: number
    preview: string
  }
  user: string
  canBeBought: boolean
}

interface CartPreorder {
  id: number
  title: string
  payable: boolean
  items: CartItem[]
}

const DEBOUNCE_DELAY = 500

export const useCartStore = defineStore('cart', () => {
  const toast = useToast()
  const modalStore = useModalStore()

  const cartData = ref<CartPreorder[]>([])
  const pendingSyncOperations = ref<Promise<any>[]>([])
  const updateTimers = ref<Record<number, ReturnType<typeof setTimeout>>>({})

  const loading = ref(false)
  const error = ref<string | null>(null)
  const creatingOrder = ref(false)
  const authError = ref(false)
  const donError = ref(false)

  const clearErrors = () => {
    error.value = null
    authError.value = false
    donError.value = false
  }

  const handleApiError = (err: unknown) => {
    const apiError = err as { message: string; code?: number; status?: number }
    error.value = apiError.message

    if (apiError.code === 401 || apiError.status === 401) {
      authError.value = true
      modalStore.open('auth')
    } else if (apiError.code === 403 || apiError.status === 403) {
      donError.value = true
    }

    if (apiError.message && apiError.code == 400) {
      toast.error(apiError.message, { position: 'top' })
    } else {
      toast.error("Что-то пошло не так. Зайдите позже.", { position: 'top' })
    }
    console.error('[CartStore] ошибка:', apiError)
  }

  const addToCart = async (merch: Merch, preorder: PreOrder) => {
    try {
      let cartPreOrder = findPreOrderById(preorder.id)

      if (!cartPreOrder) {
        cartPreOrder = {
          id: preorder.id,
          title: preorder.title,
          payable: preorder.isPaymentEnabled,
          items: [],
        }
        cartData.value.push(cartPreOrder)
      }

      const item = {
        amount: 1,
        canBeBought: true,
        id: -1,
        merch,
      }
      cartPreOrder.items.push(item)
      item.id = (await cartApi.add(merch.id, 1)).id
      toast.success('Товар добавлен в корзину', { position: 'top' })
    } catch (err) {
      handleApiError(err)
      await sync(true)
      throw err
    }
  }

  const sync = async (force = false) => {
    loading.value = true
    clearErrors();

    try {
      cartData.value = await cartApi.load(force)
    } catch (err) {
      handleApiError(err)
      cartData.value = []
      throw err
    } finally {
      loading.value = false
    }
  }

  const remove = async (itemId: number) => {
    try {
      const item = findItemById(itemId)
      if (!item) return

      const preorderIndex = cartData.value.findIndex((p) => p.items.some((i) => i.id === itemId))
      if (preorderIndex === -1) return

      cartData.value[preorderIndex].items = cartData.value[preorderIndex].items.filter(
        (i) => i.id !== itemId,
      )

      if (cartData.value[preorderIndex].items.length === 0) {
        cartData.value.splice(preorderIndex, 1)
      }

      const syncPromise = cartApi.remove(itemId)
      pendingSyncOperations.value.push(syncPromise)
      await syncPromise.finally(() => {
        pendingSyncOperations.value = pendingSyncOperations.value.filter((p) => p !== syncPromise)
      })
      toast.success('Товар удален из корзины', { position: 'top' })
    } catch (err) {
      await sync(true)
      handleApiError(err)
      throw err
    }
  }

  const removeMany = async (merchIds: number[], announce: boolean = false) => {
    try {
      merchIds.forEach((merchId) => {
        const item = findItemByMerchId(merchId)
        if (!item) return

        const preorderIndex = cartData.value.findIndex((p) => p.items.some((i) => i.id === item.id))
        if (preorderIndex === -1) return

        cartData.value[preorderIndex].items = cartData.value[preorderIndex].items.filter(
          (i) => i.id !== item.id,
        )

        if (cartData.value[preorderIndex].items.length === 0) {
          cartData.value.splice(preorderIndex, 1)
        }
      })

      const syncPromise = cartApi.removeMany(merchIds)
      pendingSyncOperations.value.push(syncPromise)
      await syncPromise.finally(() => {
        pendingSyncOperations.value = pendingSyncOperations.value.filter((p) => p !== syncPromise)
      })
      if (announce) {
        toast.success('Товар удален из корзины', { position: 'top' })
      }
    } catch (err) {
      await sync(true)
      handleApiError(err)
      throw err
    }
  }

  const updateItemAmount = async (cartItemId: number, newAmount: number) => {
    try {
      if (!Number.isInteger(newAmount)) {
        throw { message: 'Количество должно быть целым числом', code: 400 }
      }

      if (newAmount > 99) {
        toast.error('Максимальное количество товара - 99', { position: 'top' })
        newAmount = 99
      }

      const item = findItemById(cartItemId)
      if (!item) throw { message: 'Товар не найден в корзине', code: 404 }

      if (item.merch.leftAmount != -1000 && item.merch.leftAmount < newAmount) {
        if (item.merch.leftAmount == 0) {
          toast.error(`Товар отсутствует.`, { position: 'top' })
        } else {
          toast.error(`Недостаточно товара (осталось ${item.merch.leftAmount} шт.)`, { position: 'top' })
        }
        newAmount = item.merch.leftAmount
        if (newAmount < 1) {
          return
        }
      }

      if (newAmount < 1) {
        await remove(cartItemId)
        return
      }

      const oldAmount = item.amount
      item.amount = newAmount

      if (updateTimers.value[cartItemId]) {
        clearTimeout(updateTimers.value[cartItemId])
      }

      updateTimers.value[cartItemId] = setTimeout(async () => {
        try {
          const syncPromise = syncUpdateItemAmount(item.merch.id, newAmount).catch(async (err) => {
            item.amount = oldAmount
            await sync(true)
            throw err
          })

          pendingSyncOperations.value.push(syncPromise)
          await syncPromise.finally(() => {
            pendingSyncOperations.value = pendingSyncOperations.value.filter(
              (p) => p !== syncPromise,
            )
          })
        } catch (err) {
          handleApiError(err)
          throw err
        }
      }, DEBOUNCE_DELAY)
    } catch (err) {
      handleApiError(err)
      throw err
    }
  }

  const syncUpdateItemAmount = async (merchId: number, amount: number) => {
    return cartApi.update(merchId, amount)
  }

  const findItemById = (id: number): CartItem | undefined => {
    for (const preorder of cartData.value) {
      const item = preorder.items.find((i) => i.id === id)
      if (item) return item
    }
    return undefined
  }

  const findItemByMerchId = (merchId: number): CartItem | undefined => {
    for (const preorder of cartData.value) {
      const item = preorder.items.find((i) => i.merch.id === merchId)
      if (item) return item
    }
    return undefined
  }

  const findPreOrderById = (id: number): PreOrder | undefined => {
    return cartData.value.find((i) => i.id === id)
  }

  const clearTimers = () => {
    Object.values(updateTimers.value).forEach((timer) => clearTimeout(timer))
    updateTimers.value = {}
  }

  return {
    cartData,
    loading,
    error,
    authError,
    donError,
    creatingOrder,
    findItemByMerchId,
    removeMany,
    addToCart,
    sync,
    remove,
    updateItemAmount,
    clearTimers,
    clearErrors,
  }
})
